/**
 * Contact Form Module
 *
 * Handles client-side validation, AJAX submission, loading/success states,
 * and honeypot spam detection for the contact form.
 *
 * Loaded only on the /kontakt/ page as a separate Vite entry point.
 *
 * @package Sansegynge
 */

document.addEventListener('DOMContentLoaded', () => {
	initContactForm();
});

/**
 * Initialize the contact form by attaching event handlers.
 */
function initContactForm() {
	const wrapper = document.querySelector('[data-contact-form]');

	if (!wrapper) {
		return;
	}

	const form = wrapper.querySelector('form');

	if (!form) {
		return;
	}

	// Submit handler
	form.addEventListener('submit', async (e) => {
		e.preventDefault();

		if (validateForm(form)) {
			await submitForm(form, wrapper);
		}
	});

	// Real-time validation clearing on input
	const requiredFields = ['sg_name', 'sg_email', 'sg_message'];

	requiredFields.forEach((fieldName) => {
		const field = form.querySelector(`[name="${fieldName}"]`);

		if (!field) {
			return;
		}

		field.addEventListener('input', () => {
			clearFieldError(form, fieldName);
		});
	});
}

/**
 * Validate the form fields client-side.
 *
 * @param {HTMLFormElement} form The form element.
 * @return {boolean} True if all fields are valid.
 */
function validateForm(form) {
	// Clear all previous errors first
	clearAllErrors(form);

	let isValid = true;

	// Validate Navn
	const name = form.querySelector('[name="sg_name"]');

	if (name && !name.value.trim()) {
		showFieldError(form, 'sg_name', 'Indtast venligst dit navn');
		isValid = false;
	}

	// Validate Email
	const email = form.querySelector('[name="sg_email"]');

	if (email) {
		const emailValue = email.value.trim();

		if (!emailValue) {
			showFieldError(form, 'sg_email', 'Indtast venligst din email');
			isValid = false;
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
			showFieldError(form, 'sg_email', 'Indtast venligst en gyldig emailadresse');
			isValid = false;
		}
	}

	// Validate Besked
	const message = form.querySelector('[name="sg_message"]');

	if (message && !message.value.trim()) {
		showFieldError(form, 'sg_message', 'Indtast venligst din besked');
		isValid = false;
	}

	return isValid;
}

/**
 * Submit the form via AJAX to admin-ajax.php.
 *
 * @param {HTMLFormElement} form    The form element.
 * @param {HTMLElement}     wrapper The data-contact-form wrapper.
 */
async function submitForm(form, wrapper) {
	const ajaxUrl = form.getAttribute('data-ajax-url');
	const nonce = form.getAttribute('data-nonce');
	const submitBtn = form.querySelector('[data-submit-btn]');
	const formError = form.querySelector('[data-form-error]');

	// Check honeypot: if filled, silently show success (bot detected)
	const honeypot = form.querySelector('[name="sg_website"]');

	if (honeypot && honeypot.value) {
		showSuccess(form, wrapper);
		return;
	}

	// Build form data
	const formData = new FormData(form);
	formData.append('action', 'sg_contact_submit');
	formData.append('nonce', nonce);

	// Set loading state
	setLoadingState(submitBtn, true);

	// Hide any previous error
	if (formError) {
		formError.classList.add('hidden');
		formError.textContent = '';
	}

	try {
		const response = await fetch(ajaxUrl, {
			method: 'POST',
			body: formData,
		});

		const data = await response.json();

		if (data.success) {
			showSuccess(form, wrapper);
		} else {
			const errorMessage = data.data && data.data.message
				? data.data.message
				: 'Der opstod en fejl. Prov venligst igen.';

			if (formError) {
				formError.textContent = errorMessage;
				formError.classList.remove('hidden');
			}

			setLoadingState(submitBtn, false);
		}
	} catch {
		if (formError) {
			formError.textContent = 'Der opstod en fejl. Prov venligst igen.';
			formError.classList.remove('hidden');
		}

		setLoadingState(submitBtn, false);
	}
}

/**
 * Show an error message for a specific field.
 *
 * @param {HTMLFormElement} form      The form element.
 * @param {string}         fieldName The field name attribute value.
 * @param {string}         message   The error message to display.
 */
function showFieldError(form, fieldName, message) {
	const field = form.querySelector(`[name="${fieldName}"]`);
	const errorEl = form.querySelector(`[data-error="${fieldName}"]`);

	if (field) {
		field.classList.add('border-red-500');
	}

	if (errorEl) {
		errorEl.textContent = message;
		errorEl.classList.remove('hidden');
	}
}

/**
 * Clear the error state for a specific field.
 *
 * @param {HTMLFormElement} form      The form element.
 * @param {string}         fieldName The field name attribute value.
 */
function clearFieldError(form, fieldName) {
	const field = form.querySelector(`[name="${fieldName}"]`);
	const errorEl = form.querySelector(`[data-error="${fieldName}"]`);

	if (field) {
		field.classList.remove('border-red-500');
	}

	if (errorEl) {
		errorEl.textContent = '';
		errorEl.classList.add('hidden');
	}
}

/**
 * Clear all field error states.
 *
 * @param {HTMLFormElement} form The form element.
 */
function clearAllErrors(form) {
	form.querySelectorAll('[data-error]').forEach((el) => {
		el.textContent = '';
		el.classList.add('hidden');
	});

	form.querySelectorAll('.border-red-500').forEach((el) => {
		el.classList.remove('border-red-500');
	});
}

/**
 * Toggle the submit button loading state.
 *
 * @param {HTMLButtonElement} btn     The submit button.
 * @param {boolean}          loading Whether to show loading state.
 */
function setLoadingState(btn, loading) {
	if (!btn) {
		return;
	}

	if (loading) {
		btn.disabled = true;
		btn.innerHTML = '<svg class="inline-block animate-spin mr-2" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" stroke-dasharray="31.4 31.4" stroke-linecap="round"/></svg>Sender...';
		btn.classList.add('opacity-70');
	} else {
		btn.disabled = false;
		btn.innerHTML = 'Send besked';
		btn.classList.remove('opacity-70');
	}
}

/**
 * Show the success message and hide the form.
 *
 * @param {HTMLFormElement} form    The form element.
 * @param {HTMLElement}     wrapper The data-contact-form wrapper.
 */
function showSuccess(form, wrapper) {
	form.classList.add('hidden');

	const successEl = wrapper.querySelector('[data-contact-form-success]');

	if (successEl) {
		successEl.classList.remove('hidden');
	}
}
